import React, { useEffect, useState, useRef, useCallback, Fragment, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import moment from 'moment';

//Components
import { Button, Form, Select, ToggleSwitch, ContactsSelector } from 'Components';
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
import { NotificationManager } from 'Components/Notifications';
import FromToMap from 'Components/Map/components/FromToMap';
import { DatePicker, TextArea } from "@gpstrackit/gtc-react-controls";
import Preferences from 'Core/containers/Preferences';
import { Grid, Icon } from '@mui/material';

//Helpers
import { copyToClipboard, getRouteInfo } from 'Core/data/Helpers';
import { endpoints } from 'Core/defaultValues';
import { getNotesCounterColor, NOTES_MAX_LENGTH } from '../../helpers';

/** SVG icon for clock - color is injected dynamically */
const buildClockSvg = (color = '#005bf8') => `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;

/** SVG icon for route/distance */
const routeSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="5" r="3"/><line x1="12" y1="8" x2="12" y2="16"/><circle cx="12" cy="19" r="3"/></svg>`;

/** Builds the HTML content for the ETA InfoWindow at the vehicle start point */
const buildEtaLabelContent = (etaText, distanceText, vehicleLabel) => {
    return `<div class="eta-marker-label">
        ${vehicleLabel ? `<div class="eta-marker-label__header">
            <span class="eta-marker-label__vehicle">${vehicleLabel}</span>
        </div>` : ''}
        <div class="eta-marker-label__row eta-marker-label__row--main">
            ${buildClockSvg()}
            <span class="eta-marker-label__time" style="color:#005bf8">${etaText}</span>
        </div>
        ${distanceText ? `<div class="eta-marker-label__row">
            ${routeSvg}
            <span class="eta-marker-label__distance">${distanceText}</span>
        </div>` : ''}
    </div>`;
};

//Enums
import { TypeObjectMap } from 'Components/Map/providers/enums';

//Hooks
import { useGeocoder } from 'Core/hooks';

import { createSelector } from 'reselect'

import { useDataGridThrottler } from 'Core/hooks'
import { unitSelectors } from 'Core/realtime/redux/unitSlice';
import { lastReadingSelectors } from 'Core/realtime/redux/lastReadingSlice';
import { getUnitsNaturalOrder } from 'Core/data/Helpers';

const getUnitsCatalog = createSelector([state => unitSelectors.selectAll(state)], getUnitsNaturalOrder);
const getReadingsCatalog = (state) => lastReadingSelectors.selectEntities(state);
const getVehicleTags = (state) => state.realtimeMapUtilitiesVehiclesListRedux.tagsVehicleList;

/*********************************************************
 * Get information from pagination elements
*********************************************************/
const getData = createSelector(
    [getUnitsCatalog, getReadingsCatalog, getVehicleTags],
    (unitsList, readings, vehicleTags) => {
        let result = []
        unitsList?.forEach((unit) => {
            try {
                if (unit.id == "-1" || (!unit.id && !unit.CUnits_id)) return
                const idUnit = unit.id

                let lastReading = readings?.[idUnit]
                const lat = lastReading?.latitude || unit?.latitude || unit?.lat
                const lng = lastReading?.longitude || unit?.longitude || unit?.lng
                const label = unit?.label || lastReading?.deviceLabel;

                if (!Array.isArray(unit?.tags) || unit.tags.length === 0) {
                    unit = { ...unit, tags: [{ color: "000000", hasChildren: false, id: -1, label: "Untagged", parentId: null, path: "" }] }
                }

                //filter unit for existing tag
                unit = {
                    ...unit,
                    tags: !vehicleTags?.length ? unit.tags : unit.tags?.filter(tag => vehicleTags?.some(vehicleTag => tag?.id == -1 || vehicleTag?.id === tag?.id))
                }

                // if the unit no have tags or none is the group "Untagged", it is omitted
                if (!unit?.tags?.length && !unit?.tags?.some(tag => tag.id === -1)) return; // No is entered on the list of vehicle because no have permisions

                if (!lastReading?.haveLatLng) return; // No is entered on the list of vehicle because no have lat and lng

                const item = {
                    id: unit.id || lastReading?.deviceId,
                    label: label,
                    lat: lat ? parseFloat(lat) : 0,
                    lng: lng ? parseFloat(lng) : 0,
                    latitude: lat ? parseFloat(lat) : 0,
                    longitude: lng ? parseFloat(lng) : 0,
                    haveLatLng: lastReading?.haveLatLng, // this vehicle no have lat and lng, cant show on map
                    address: lastReading?.address,
                    city: lastReading?.address?.city || '',
                    state: lastReading?.address?.state || '',
                    fulladdress: lastReading?.address?.formatted || '',
                    changeLabel: (unit?.label != label),
                    changeLatLng: lastReading?.changeLatLng,
                    driverName: unit?.driverName,
                    lastReading: lastReading,
                    tags: unit.tags || []
                }
                result.push(item);
            } catch (e) {
                console.log('e', e)
                NotificationManager.error('Error', `Error src/components/Map/components/FromToMap/index.js to create show list of vehicles - ${e}`, 3000, null, null, 'error');
            }
        });
        return result
    }
)

//Actions
import {
    setMapRoutesOptionsRestart,
    setMapRoutesOptions,
    drawGetDirections,
    catalogLoad,
    addPointers,
} from 'Redux/actions';
import {
    realtimeMapsEtaGetMarkerDetail,
    realtimeMapsEtaCreateEtaLink,
    realtimeMapsEtaEditEtaLink,
    etaCreateEditSetField,
    etaCreateEditReset
} from './redux/slice';

const EtaCreateEdit = (props) => {
    const dispatch = useDispatch();
    const { mapRef } = props;
    const { messages } = useIntl();

    //Ref's
    const shareEtaLinkForm = useRef();
    const preferencesRef = useRef();
    const etaFromToMapRef = useRef();
    const etaInfoWindowRef = useRef(null);
    //Hook's
    const [onGetGeocoder, address, setAddress] = useGeocoder();

    //Variables
    const routeProperties = {
        optimizeWaypoints: true,
        avoidTolls: false,
        avoidHighways: false,
    }

    /*********************************************************
     * use compute data to sync values showing in table
    *********************************************************/
    const computedData = useSelector(state => getData(state) || []);
    const propertiesToValidate = useMemo(() => ["label", "lat", "lng", "changeLatLng", "haveLatLng", "tags"], []);

    // Hook handles both Throttled Arrays for `<Map/>` and imperative delta diffing for markers via `handleMapUpdates`
    const units = useDataGridThrottler(
        computedData,
        1000,
        () => { },
        "id",
        propertiesToValidate
    );

    //Redux States
    const loadingUnits = useSelector(state => state.catalogsRedux.loadingUnits);
    const contacts = useSelector(state => state.catalogsRedux.contacts);
    const loadingContacts = useSelector(state => state.catalogsRedux.loadingContacts);

    const { open, loading, create, error, eta, edit, loadingMarkerDetail, markerDetail } = useSelector(state => state.realtimeMapEtaLinkCreateEditRedux);
    const currentModule = useSelector(state => state.realtimeMapUtilitiesRedux.currentModule);
    const { pointers, directionsError } = useSelector((state) => state.realtimeMapRedux);
    const { user: { brandDomains, isKph } } = useSelector((state) => state.securityRedux);

    //Local States
    const [loadPoint, setLoadPoint] = useState(true);
    const [formData, setFormData] = useState({ hasVideo: false });
    const [country, setCountry] = useState(null);
    const [currenStartDate, setCurrentStartDate] = useState(true);
    const [currenEndDate, setCurrentEndDate] = useState(true);
    const [routeInfo, setRouteInfo] = useState({ eta: null, distance: null });

    const createRouteOnView = (startPoint, endPoint) => {
        const tempPointers = [
            {
                id: '1',
                lat: parseFloat(startPoint?.latitude || 0),
                lng: parseFloat(startPoint?.longitude || 0),
                label: startPoint?.driverName && startPoint?.driverName != "true" && startPoint?.driverName != "null"
                    ?
                    `${startPoint?.label} (${startPoint?.driverName})` : startPoint?.label,
                heading: 0,
                color: '000',
                eventName: 'no event',
                draggable: false,
                updatePointers: true,
                hideMarker: true
            },
            {
                id: '2',
                lat: parseFloat(endPoint?.latitude || 0),
                lng: parseFloat(endPoint?.longitude || 0),
                label: messages['eta_destination'],
                heading: 0,
                color: '000',
                eventName: 'no event',
                draggable: false,
                updatePointers: true,
                hideMarker: true
            }
        ];

        dispatch(drawGetDirections(false))
        //save pointers and shows into map
        dispatch(addPointers([...tempPointers]));
        dispatch(setMapRoutesOptions(routeProperties));
    }

    const handleGetRouteInfo = useCallback((startPoint, endPoint) => {
        getRouteInfo(startPoint, endPoint, {
            isKph,
            messages,
            onResult: ({ eta: etaText, distance }) => {
                setRouteInfo({ eta: etaText, distance });
                const vehicleLabel = startPoint?.driverName && startPoint?.driverName !== 'true' && startPoint?.driverName !== 'null'
                    ? `${startPoint?.label}`
                    : startPoint?.label;
                const content = buildEtaLabelContent(etaText, distance, vehicleLabel);
                const position = {
                    lat: parseFloat(startPoint?.latitude || 0),
                    lng: parseFloat(startPoint?.longitude || 0),
                };
                if (!etaInfoWindowRef.current) {
                    etaInfoWindowRef.current = new google.maps.InfoWindow({
                        content,
                        disableAutoPan: true,
                        pixelOffset: new google.maps.Size(0, -40),
                    });
                    etaInfoWindowRef.current.setPosition(position);
                    etaInfoWindowRef.current.open({ map: mapRef?.current?.mapItem, shouldFocus: false });
                } else {
                    etaInfoWindowRef.current.setContent(content);
                    etaInfoWindowRef.current.setPosition(position);
                    if (!etaInfoWindowRef.current.getMap()) {
                        etaInfoWindowRef.current.open({ map: mapRef?.current?.mapItem, shouldFocus: false });
                    }
                }
            },
        });
    }, [isKph, messages]);

    const isShareButtonDisabled = () => {
        if (!formData?.destination?.address || !formData?.destination?.latitude || !formData?.destination?.longitude) {
            return true;
        }

        if (!formData?.unit?.latitude || !formData?.unit?.longitude) {
            return true;
        }

        if (pointers?.length < 2) {
            return true
        }

        if (!formData?.contacts?.length) {
            return true;
        }
        return false;
    }

    const onSubmit = useCallback(() => {
        if (!formData?.destination?.address || !formData?.destination?.latitude || !formData?.destination?.longitude) {
            NotificationManager.error(
                messages['eta_link_error_destination'],
                messages['error'],
                3000,
                null,
                null,
                'warning',
            );
            return;
        }

        if (pointers.length == 2 && !formData?.contacts?.length) {
            NotificationManager.error(
                messages['eta_link_error_recipient'],
                messages['error'],
                3000,
                null,
                null,
                'warning',
            );
            return;
        }

        let etaValue = parseFloat((mapRef?.current?.getDurationRoute() / 3600).toFixed(1));
        let start = moment(formData.startDate)
        let end = moment(formData.endDate)
        start = moment(start).set("second", 59).toISOString()
        end = moment(end).set("second", 59).toISOString()

        if (currenStartDate && !eta) {
            start = moment().set("second", 59).toISOString()
            if (currenEndDate) {
                end = moment(start).add(8, "hours")
            }
        }

        if (start > end) {
            NotificationManager.error(
                messages['eta_title_date_less_error'],
                messages['error'],
                3000,
            );
            return
        }

        if (eta) {
            let currentStartDate = moment(eta?.startDate).set("second", 59).toISOString()
            if (currentStartDate !== start) {
                let currentDate = moment().utc();
                if (start < currentDate.format()) {
                    NotificationManager.error(
                        messages['eta_title_error_current_date'],
                        messages['error'],
                        3000,
                    );
                    return
                }
            }
        }

        let dateWeek = moment(start).add(1, 'week').toISOString()
        if (end > dateWeek) {
            NotificationManager.error(
                messages['eta_title_limit_end_date'],
                messages['error'],
                3000,
            );
            return
        }
        if (!eta) {
            let currentDate = moment().utc();
            if (start < currentDate.format()) {
                NotificationManager.error(
                    messages['eta_title_error_current_date'],
                    messages['error'],
                    3000,
                );
                return
            }
        }

        let newStart = moment(start)
        newStart = moment(newStart).set("second", 0).toISOString()

        let dateRange = {
            start: newStart,
            end: end
        }

        let etaLink = {
            unitId: formData?.unit?.id,
            entityId: formData?.unit?.id,
            entityName: "Unit",
            landmarkId: formData?.destination?.landmarkId,
            address: formData?.destination?.address,
            latitude: formData?.destination?.latitude,
            longitude: formData?.destination?.longitude,
            eta: `${etaValue || 0}`,
            sharedWith: {
                contacts: formData?.contacts?.filter(c => c?.type === "CONTACT")?.map(c => parseInt(c?.id)) || [],
                emails: formData?.contacts?.filter(c => c?.type === "EMAIL")?.map(c => c?.value) || [],
                sms: formData?.contacts?.filter(c => c?.type === "SMS")?.map(c => `${c?.value}`) || [],
            },
            scheduleSnapshot: formData?.scheduleSnapshot || false,
            dateRange: dateRange,
            notes: formData?.notes
        };

        if (eta) {
            etaLink = {
                id: eta?.id,
                ...etaLink
            }
            dispatch(realtimeMapsEtaEditEtaLink(etaLink))
        } else {
            dispatch(realtimeMapsEtaCreateEtaLink(etaLink));
        }
    });

    const onChangeSelectItemDestination = () => {
        setFormData({ ...formData, destination: null });
        setRouteInfo({ eta: null, distance: null });
        mapRef?.current?.deleteRouteInMap();
    }

    const handleClose = () => {
        if (etaInfoWindowRef.current) {
            etaInfoWindowRef.current.close();
            etaInfoWindowRef.current = null;
        }
        dispatch(etaCreateEditReset());
        setLoadPoint(true);
        setCountry(null);
        setFormData({});
        setRouteInfo({ eta: null, distance: null });
        clearForm();
        setCurrentStartDate(true);
        setCurrentEndDate(true);
    }

    useEffect(() => {
        if (open && !eta) {
            let startDate = moment()
            let endDate = moment(startDate).add(8, "hours")
            setFormData({ ...formData, startDate, endDate })
        }
        if (open && eta) {
            let currentContacts = []
            let currentUnit = units.find((item) => item.id === eta?.unitId)
            if (eta.sharedWith?.contacts?.length) {
                eta.sharedWith?.contacts.map((item) => {
                    let currentContact = contacts.find((contact) => contact?.id === item)
                    if (currentContact?.id) {
                        currentContacts.push({
                            id: currentContact?.id,
                            value: currentContact?.address,
                            type: "CONTACT",
                            contactType: currentContact?.type
                        })
                    }
                })
            }
            if (eta.sharedWith?.emails?.length) {
                eta.sharedWith?.emails.map((item) => {
                    currentContacts.push({
                        id: item,
                        value: item,
                        type: "EMAIL",
                        contactType: ""
                    })
                })
            }
            if (eta.sharedWith?.sms?.length) {
                eta.sharedWith?.sms.map((item) => {
                    currentContacts.push({
                        id: item,
                        value: item,
                        type: "SMS",
                        contactType: ""
                    })
                })
            }
            dispatch(realtimeMapsEtaGetMarkerDetail({ id: eta?.unitId }))
            setFormData({
                unit: {
                    ...currentUnit,
                    latitude: currentUnit?.latitude,
                    longitude: currentUnit?.longitude,
                    driverName: currentUnit?.driverName || null
                },
                contacts: currentContacts,
                startDate: moment.utc(eta?.startDate).format(),
                endDate: moment.utc(eta?.expirationDate).format(),
                scheduleSnapshot: eta?.scheduleSnapshot,
                destination: {
                    address: eta?.address,
                    latitude: eta?.latitude,
                    longitude: eta?.longitude,
                },
                notes: eta?.notes
            })
            if (eta?.latitude && eta?.longitude) {
                onGetGeocoder({
                    from: 'latlng',
                    lat: eta?.latitude,
                    lng: eta?.longitude
                });
            }
        }
    }, [currentModule, open, contacts, units])

    useEffect(() => {
        if (formData?.unit?.latitude && formData?.unit?.longitude && formData?.destination?.latitude && formData?.destination?.longitude) {
            createRouteOnView(formData?.unit, formData?.destination);
            handleGetRouteInfo(formData?.unit, formData?.destination);
        } else {
            setRouteInfo({ eta: null, distance: null });
            if (etaInfoWindowRef.current) {
                etaInfoWindowRef.current.close();
                etaInfoWindowRef.current = null;
            }
        }
    }, [formData?.unit?.latitude, formData?.unit?.longitude, formData?.destination?.latitude, formData?.destination?.longitude]);

    useEffect(() => {
        if (directionsError > 0) {
            dispatch(addPointers([]));
        }
    }, [directionsError])

    useEffect(() => {
        if (address && address?.result == true) {
            const { data } = address;
            if (data && data.length > 0) {
                let destination = {
                    ...formData?.destination,
                    address: data[0].fulladdress,
                }
                if (loadPoint && eta?.latitude && eta?.longitude) {
                    setLoadPoint(false)
                    destination = {
                        ...destination,
                        latitude: eta?.latitude,
                        longitude: eta?.longitude,
                    }
                }
                setFormData({ ...formData, destination: destination });
                dispatch(etaCreateEditSetField({ loading: false }))
            }
        }
    }, [address]);

    useEffect(() => {
        if (pointers && pointers.length > 1) {
            mapRef?.current?.deleteRouteInMap()
            dispatch(drawGetDirections(true))
        }
    }, [pointers]);

    useEffect(() => {
        if (directionsError > 0) {
            dispatch(addPointers([]));
        }
    }, [directionsError])

    useEffect(() => {
        if (create || edit) {
            if (create?.status === "Active" || edit?.status === "Active") {
                NotificationManager.success(
                    messages['eta_link_success'],
                    messages['success'],
                    3000,
                    null,
                    null,
                    'success',
                );
                let url = `${endpoints?.CLOUD_URL}/etaLink/${create?.code || edit?.code}`;
                if (brandDomains && process.env.STACK === "production") {
                    try {
                        let domains = JSON.parse(brandDomains)
                        if (domains.length) {
                            url = `https://${domains[0]}/etaLink/${create?.code || edit?.code}`
                        }
                    } catch (error) { }
                }
                copyToClipboard(url);
                handleClose();
            }
            if (create?.status === "Scheduled" || edit?.status === "Scheduled") {
                NotificationManager.success(
                    edit ? messages['eta_title_edit_scheduled'] : messages['eta_title_creation_scheduled'],
                    messages['success'],
                    3000,
                    null,
                    null,
                    'success',
                );
                handleClose();
            }
        }
    }, [create, edit])

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                messages['eta_link_error'],
                messages['error'],
                3000,
                null,
                null,
                'warning',
            );
            dispatch(etaCreateEditReset({ error: false, create: null, edit: null }))
        }
    }, [error])

    useEffect(() => {
        if (markerDetail?.id) {
            if (markerDetail?.markerDetail?.linkedDevice?.hasVideo
                || markerDetail?.markerDetail?.unit?.manufacturerName?.toUpperCase() === 'LIGHTMETRICS') {
                setFormData({ ...formData, hasVideo: true })
            } else {
                setFormData({ ...formData, hasVideo: false })
            }
        }
    }, [markerDetail])

    const clearForm = () => {
        mapRef?.current?.deleteRouteInMap();
        dispatch(drawGetDirections(false));
        dispatch(addPointers([]));
    }

    useEffect(() => {
        if (open) {
            dispatch(setMapRoutesOptionsRestart());
            dispatch(catalogLoad(['contacts']));
            clearForm();
        }
    }, [open])

    const currentDate = moment();
    const futureDate = currentDate.add(2, 'months');

    return (
        <Modal
            title={eta ? messages['eta_title_edit'] : messages['eta_title_create']}
            open={open}
            loading={loading || loadingContacts || loadingUnits || loadingMarkerDetail}
            position="right"
            size="small"
            className="modal-top-0"
            headerStyle={{ top: 0 }}
            handleClose={handleClose}
        >
            <ModalBody>
                <div style={{ padding: '18px 24px 24px 24px' }}>
                    <p style={{ marginTop: -10 }} className='text-muted'>{messages['eta_subtitle']}</p>
                    <Preferences
                        ref={preferencesRef}
                        preferencesKey="etaLink"
                        onInitialPreferences={(preferences) => {
                            if (preferences?.country) {
                                setCountry(preferences?.country);
                            }
                        }}
                    >
                        <Fragment>
                            <Form ref={shareEtaLinkForm} onValidSubmit={onSubmit}>
                                <div style={{ marginBottom: 10 }}>
                                    <h3 style={{ marginBottom: 0 }}>{messages['eta_vehicle']}</h3>
                                    <Select
                                        id="unit"
                                        name="unit"
                                        customLabel={
                                            <Grid
                                                container
                                                direction="row"
                                                justifyContent="flex-start"
                                                alignItems="center"
                                            >
                                                <Grid item>
                                                    <Icon
                                                        style={{
                                                            color: '#DF1A31',
                                                            fontSize: 16,
                                                            marginTop: 2,
                                                            marginRight: 2,
                                                            marginLeft: -3
                                                        }}
                                                    >
                                                        location_on_icon
                                                    </Icon>
                                                </Grid>
                                                <Grid item>
                                                    {messages['eta_choose_vehicle']} <span className="text-danger"> *</span>
                                                </Grid>
                                            </Grid>
                                        }
                                        placeholder={messages['eta_type_to_search']}
                                        value={formData?.unit}
                                        onChange={(newUnit) => {
                                            const { driverName, lastReading } = newUnit;
                                            const { latitude, longitude } = lastReading;
                                            mapRef?.current?.centerObjectOnMap(TypeObjectMap.Marker, newUnit?.id, 12);
                                            setFormData({
                                                ...formData,
                                                unit: {
                                                    ...newUnit,
                                                    latitude: latitude,
                                                    longitude: longitude,
                                                    driverName: driverName || null
                                                },
                                                hasVideo: false,
                                                scheduleSnapshot: false
                                            });
                                            dispatch(etaCreateEditReset({ markerDetail: null }));
                                            dispatch(realtimeMapsEtaGetMarkerDetail({ id: newUnit?.id }))
                                        }}
                                        disabled={eta || loading || (create?.id ? true : false)}
                                        loading={loadingUnits}
                                        options={units}
                                        textField="label"
                                        valueField="id"
                                        isSearchable
                                        required
                                        requiredMessage={messages['required_field']}
                                    />
                                </div>
                                {
                                    formData.hasVideo &&
                                    <div style={{ marginBottom: 15 }}>
                                        <ToggleSwitch
                                            id="custom-scheduleSnapshot"
                                            label={messages['eta_type_share_camera_snapshot']}
                                            small
                                            blueToggle
                                            optionLabels={['', '']}
                                            checked={formData?.scheduleSnapshot || false}
                                            onChange={(checked) => {
                                                setFormData({ ...formData, scheduleSnapshot: checked });
                                            }}
                                        />
                                    </div>
                                }
                                <div style={{ marginBottom: 15 }}>
                                    <h3 style={{ marginBottom: 5 }}>{messages['eta_destination']}</h3>
                                    <label className="input-normal-label">
                                        <Grid
                                            container
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                        >
                                            <Grid item>
                                                <Icon
                                                    style={{
                                                        color: '#00B200',
                                                        fontSize: 16,
                                                        marginTop: 2,
                                                        marginRight: 2,
                                                        marginLeft: -3
                                                    }}
                                                >
                                                    location_on_icon
                                                </Icon>
                                            </Grid>
                                            <Grid item>
                                                {messages['eta_choose_destination']}<span className="text-danger"> *</span>
                                            </Grid>
                                        </Grid>
                                    </label>

                                    <FromToMap
                                        ref={etaFromToMapRef}
                                        hideClear={true}
                                        entities={["ADDRESS", "MAP_CLICK", "LANDMARK"]}
                                        disabled={loading || !formData?.unit}
                                        onGetAddress={(newPosition) => {
                                            const currentPosition = newPosition[0];
                                            setFormData({
                                                ...formData,
                                                destination: {
                                                    ...currentPosition,
                                                    address: currentPosition?.fulladdress,
                                                    latitude: currentPosition?.lat,
                                                    longitude: currentPosition?.lng,
                                                }
                                            });
                                        }}
                                        onGetPosition={(newPosition) => {
                                            dispatch(etaCreateEditSetField({ loading: true }))
                                            onGetGeocoder({
                                                from: 'latlng',
                                                lat: newPosition?.lat,
                                                lng: newPosition?.lng
                                            });
                                            setFormData({
                                                ...formData,
                                                destination: {
                                                    latitude: newPosition?.lat,
                                                    longitude: newPosition?.lng,
                                                }
                                            });
                                        }}
                                        onSelectLandmark={(newLandmark) => {
                                            setFormData({
                                                ...formData,
                                                destination: {
                                                    landmarkId: newLandmark?.id,
                                                    address: newLandmark?.address,
                                                    latitude: newLandmark?.lat,
                                                    longitude: newLandmark?.lng,
                                                }
                                            });
                                        }}
                                        onChangeSelectItem={onChangeSelectItemDestination}
                                        messages={messages}
                                        required={true}
                                        getCoordinates
                                        pills
                                        restart={(!pointers || pointers?.length == 0)}
                                        onKeyPress={(e) => { e.key === 'Enter' && e.preventDefault(); }}
                                    />
                                </div>
                            </Form>
                            <div>
                                <TextArea
                                    label={messages['eta_notes']}
                                    value={formData?.notes}
                                    maxLength={NOTES_MAX_LENGTH}
                                    onChange={(value) => setFormData({ ...formData, notes: value })}
                                />
                                <div style={{ marginTop: -5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: 12, opacity: 0.5 }}>{messages['eta_message_notes']}</span>
                                    <span style={{ fontSize: 12, color: getNotesCounterColor(formData?.notes?.length || 0) }}>
                                        {formData?.notes?.length || 0}/{NOTES_MAX_LENGTH}
                                    </span>
                                </div>
                            </div>
                            <div style={{
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box'
                            }}>
                                <h3 style={{ margin: '5px 0 10 0' }}>{messages['eta_recipient']}</h3>
                                <ContactsSelector
                                    label={messages['eta_choose_contact']}
                                    values={formData?.contacts}
                                    disabled={loading || (create?.id ? true : false)}
                                    required
                                    loading={loadingContacts}
                                    country={country}
                                    contacts={contacts?.map(contact => {
                                        return { ...contact, name: contact?.title }
                                    })}
                                    entities={["EMAIL", "SMS", "CONTACT"]}
                                    emailKeyPress={(e) => { e.code === 'Space' && e.preventDefault(); }}
                                    onChange={(values) => {
                                        setFormData({ ...formData, contacts: values });
                                    }}
                                    onChangeCountry={(newCountry) => {
                                        preferencesRef?.current?.savePreference("User", "etaLink", { country: newCountry?.code });
                                    }}
                                />
                            </div>
                            <div>
                                <h3 style={{ marginBottom: 15 }}>{messages['eta_title_message']}</h3>
                                <p style={{ marginTop: -10 }} className='text-muted'>{messages['eta_message']}</p>
                            </div>
                            <div>
                                <label className="input-normal-label">{messages['eta_type_start']}</label>
                                <DatePicker
                                    value={formData?.startDate}
                                    minDate={new Date()}
                                    maxDate={futureDate}
                                    calendarPosition="bottom"
                                    onChange={(newDate) => {
                                        setCurrentStartDate(false)
                                        if (newDate) {
                                            let newEndDate = moment(newDate).add(8, "hours")
                                            setFormData({ ...formData, startDate: newDate, endDate: newEndDate })
                                        }
                                    }}
                                    showButtons
                                    showTimePicker
                                />
                            </div>
                            <div style={{ marginBottom: 5 }}>
                                <label className="input-normal-label">{messages['eta_type_end']}</label>
                                <DatePicker
                                    value={formData?.endDate}
                                    minDate={new Date()}
                                    maxDate={futureDate}
                                    calendarPosition="bottom"
                                    onChange={(newDate) => {
                                        setCurrentEndDate(false)
                                        setFormData({ ...formData, endDate: newDate })
                                    }}
                                    showButtons
                                    showTimePicker
                                />
                            </div>
                        </Fragment>
                    </Preferences>
                </div>
            </ModalBody>
            <ModalFooter style={{ bottom: 50 }}>
                <Button
                    disabled={loading}
                    onClick={(e) => {
                        e.preventDefault();
                        handleClose();
                    }}
                    className="btn-cancel"
                >
                    {messages['cancel']}
                </Button>
                <Button
                    disabled={loading || isShareButtonDisabled()}
                    onClick={(e) => {
                        e.preventDefault();
                        shareEtaLinkForm?.current?.submit();
                    }}
                    className="btn-blue"
                >
                    {messages['save']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default EtaCreateEdit;